// =============================================================================
// FLOTA PANELI — jedna czysta funkcja, która mówi, CO JEST ZEPSUTE na
// stanowisku, i jedna, która ustawia stanowiska w kolejności „najpierw to,
// czym trzeba się zająć".
//
// DLACZEGO W PAKIECIE, A NIE W POWŁOCE. Do `S141` ten plik leżał
// w `apps/universe-shell` i miał jednego czytelnika — ekran floty. Odkąd alert
// wysyła `fabrica-core`, czytelników jest dwóch i są w różnych procesach.
// Przepisanie progów do usługi dałoby dwa zdania o tym samym stanowisku:
// mail „usterka" obok wiersza oznaczonego jako sprawny, rozjeżdżające się przy
// pierwszej poprawce jednej z kopii. Werdykt jest JEDEN, więc plik jest jeden.
//
// DLACZEGO OSOBNY PLIK, A NIE `PanelDiagnostics`. Karta diagnostyki odpowiada
// na pytanie „co się dzieje na TYM stanowisku" — czyta się ją, stojąc już nad
// jednym urządzeniem. Flota odpowiada na pytanie, którego dotąd nie dało się
// zadać w ogóle: „które z dwudziestu czterech stanowisk wymaga uwagi". To jest
// inne pytanie i inna miara — karta pokazuje FAKTY, flota WERDYKTY. Werdykt
// da się przetestować bez renderowania czegokolwiek i dlatego jest tutaj.
//
// KOSZT BRAKU TEGO EKRANU JEST POLICZONY I WYNOSI TYDZIEŃ. Dwa stanowiska
// ważyły na symulatorze przez tydzień, wysyłając co dwie minuty
// `waga: {zrodlo: symulator, token_agenta: brak}`. Telemetria BYŁA. Nie było
// miejsca, w którym ktoś by ją przeczytał bez otwierania karty urządzenie po
// urządzeniu — a otwiera się kartę tego urządzenia, o którym się już wie, że
// jest podejrzane. Ekran, na którym zepsute samo wypływa na górę, jest jedyną
// odpowiedzią na tę klasę porażki.
//
// TRZY STANY, NIE DWA — i to jest reguła całej telemetrii panelu (CLAUDE.md,
// „Nowe pole w pulsie"). Gałąź, której nie ma, znaczy „ta wersja nie umie
// odpowiedzieć", a nie „jest dobrze". Dlatego ocena niesie osobno `usterki`
// (wiem, że źle) i `niewiadome` (nie wiem) — a stanowisko z samymi
// niewiadomymi NIE jest zielone.
// =============================================================================

/**
 * Puls panelu, tak jak go składa `apps/fabrica-panel/lib/dane/puls.dart`
 * (migracje `497` i `543`).
 *
 * Kształt mieszka TUTAJ, a nie w komponencie karty, bo czytają go dziś trzy
 * ekrany (karta urządzenia, flota działu, flota platformy) i dwa z nich nie
 * mają z kartą nic wspólnego.
 */
export interface PanelPulsFacts {
  app?: string;
  wersja?: string;
  kod_wersji?: number;
  system?: string;
  platforma?: string;
  dart?: string;
  czas_pracy_s?: number;
  pamiec_mb?: number;
  rdzenie?: number;
  device_class?: string;
  ekran?: {
    px?: { w?: number; h?: number };
    dpi?: number;
    zrodlo_dpi?: string;
    dopasowanie?: number;
    pelna_miara?: boolean;
  };
  /**
   * `zrodlo` mówi „MAM CZYM zapytać agenta" (jest token), a nie „rozmawiam
   * z nim". Odpowiedź na to drugie niesie `polaczenie` — od `1.0.70`,
   * więc gałąź jest opcjonalna jak każda nowa.
   *
   * 2026-08-26 stanowisko 3 wysyłało `{zrodlo: agent, token_agenta: sejf}`
   * przy ekranie „Agent stanowiska nie odpowiada". Oba zdania były prawdziwe.
   * Ekran floty, który czyta wyłącznie `zrodlo`, pomalowałby ten wiersz na
   * zielono — dlatego ta gałąź powstała ZANIM flota pojechała w hale.
   */
  waga?: {
    zrodlo?: string;
    token_agenta?: string;
    protokol?: string;
    /** `polaczony` / `odmowaTokenu` / `brakNasluchu` / `blad` / `nieProbowano`. */
    polaczenie?: string;
    /**
     * Numer paczki agenta z powitania (od `1.0.83`), TYLKO przy stojącej
     * rozmowie. Panel i agent jadą jednym instalatorem pod jednym numerem,
     * więc inna liczba niż `wersja` znaczy jedno: agent nie wstał z nową
     * paczką. Na Pi do 1.0.82 działo się to po każdym OTA.
     */
    wersja_agenta?: string;
  };
  /**
   * AUTOMAT AKTUALIZACJI (od `1.0.83`) — czy wydania mają czym wejść.
   * `automat` to `StanAutomatu` z `aktualizacje.dart`: `zyje` / `martwy` /
   * `brak` / `usluga` / `nieDotyczy`. Gałąź opcjonalna jak każda nowa; brak
   * to „ta wersja nie umie odpowiedzieć", nie „żyje".
   */
  aktualizacja?: { automat?: string; tetno_s?: number };
  klatki?: { p95_ms?: number; ponad_budzet?: number; zmierzonych?: number; budzet_ms?: number };
  zegar?: { strefa?: string; offset_min?: number; rozjazd_s?: number };
  /**
   * WYWROTKI PANELU (od `1.0.67`). Gałąź jest OPCJONALNA i to nie jest
   * ostrożność na wyrost: panele jadą OTA i nie schodzą na całą halę w tej
   * samej minucie, więc stanowisko na starszej binarce nie przyśle jej wcale.
   * `undefined` znaczy „ta wersja nie umie odpowiedzieć", a `0` — „panel jest
   * zdrowy". Zlanie tych dwóch stanów w jedno zero pokazywałoby zieleń tam,
   * gdzie nie ma wiedzy — dokładnie ta usterka, którą `S129` rozbroiło
   * w `token_agenta`.
   */
  bledy?: {
    w_24h?: number;
    wywrotek_startu?: number;
    ostatni?: { o?: string; skad?: string; co?: string };
  };
  /**
   * BLE — zdolności agenta i DLACZEGO ich nie ma.
   *
   * Trzy poziomy „nie wiem", każdy z inną naprawą, i dlatego wszystkie są
   * opcjonalne osobno:
   *
   *   brak całej gałęzi ....... panel starszy niż etap 3 BLE
   *   `agent_oglasza: false` .. agent niepołączony albo starszy niż `umie`
   *   brak `stos`/`radio` ..... agent starszy niż ta diagnostyka
   *
   * `?? 'brak'` na którymkolwiek z tych poziomów pokazuje ZMIERZONY werdykt
   * tam, gdzie nie ma wiedzy — ta sama usterka, którą `S129` rozbroiło
   * w `token_agenta`, i ta sama, którą ta gałąź rozbraja dla radia.
   */
  ble?: {
    agent_oglasza?: boolean;
    zdolnosci?: string[];
    /** `dbus` | `win` | `atrapa` | `brak` */
    stos?: string;
    /** `gotowe` | `wylaczone` | `brak` | `nieznane` */
    radio?: string;
    /** DLACZEGO stosu nie ma — jedno zdanie; brak pola = agent starszy niż 1.0.88. */
    powod?: string;
    /**
     * Ostatnia znana telemetria testera BLE. Brak pola = agent starszy niż 1.0.92.
     *
     * DWA POLA, NIE JEDNO, i to jest cała treść tej gałęzi. Telemetria
     * powstaje WYŁĄCZNIE wtedy, gdy panel jest połączony z testerem — dziś po
     * odkrywce, po etapie G5 także po każdym pomiarze. Między tymi chwilami
     * liczba się nie odświeża, więc bez daty pomiaru „bateria 8 %" nie mówi,
     * czy tester zgasł wczoraj, czy właśnie go naładowano.
     *
     * Ta sama konstrukcja, co `opoznienie_ms` + `opoznienie_zmierzono_o`
     * w kartotece przyrządu (migracja `726`) i z tego samego powodu: liczba,
     * której nie da się umiejscowić w czasie, nie jest liczbą.
     */
    bateria?: {
      /** 0–100. `0` znaczy „zaraz zgaśnie" i NIE JEST tym samym co brak pola. */
      proc?: number;
      /** ms epoch pomiaru. Bez tego `proc` nie ma daty ważności. */
      o?: number;
      /** Adres testera, którego dotyczy — stanowisko może widzieć kilka. */
      adres?: string;
    };
  };
  praca?: { ekran?: string; zalogowany?: boolean; w_zadaniu?: boolean; odliczan?: number };
  lacznosc?: {
    szczebel?: string;
    w_kolejce?: number;
    kolejka_urwana?: boolean;
    cisza_s?: number;
    wiek_hali_s?: number;
  };
}

// ---------------------------------------------------------------------------
// PROGI. Każdy z nich jest decyzją, więc każdy ma tu zdanie uzasadnienia —
// liczba bez uzasadnienia jest liczbą, którą ktoś za pół roku „poprawi".
// ---------------------------------------------------------------------------

/**
 * Panel bije co pięć minut (`okresPulsu`). Dwanaście minut to dwie zgubione
 * tury z zapasem — jeden pakiet zgubiony po drodze nie jest awarią stanowiska
 * i nie ma prawa zapalać alarmu na liście.
 */
export const CISZA_MIN = 12;

/** Godzina ciszy to już nie zgubiony pakiet, tylko wyłączone stanowisko. */
export const MILCZY_MIN = 60;

/** Ta sama granica, co `progRozjazduZegaraS` w panelu: minuta przestawia rekord. */
export const ROZJAZD_PROG_S = 60;

/**
 * Jeden błąd na dobę zdarza się przy zerwanym Wi-Fi i nie jest usterką.
 * Dziesięć w dobę znaczy, że coś się powtarza — a powtarzalne jest naprawialne.
 */
export const BLEDOW_24H_USTERKA = 10;

/**
 * Skrzynka nadawcza rośnie, kiedy stanowisko nie ma łączności. Pięćdziesiąt
 * naważeń w kolejce to pół zmiany zapisanej wyłącznie lokalnie.
 */
export const W_KOLEJCE_UWAGA = 50;

/**
 * Jedno wydanie w tyle jest normą przez pierwszą dobę po wydaniu — OTA nie
 * schodzi na całą halę w tej samej minucie. Trzy wydania w tyle to już nie
 * opóźnienie, tylko zatrzymany OTA.
 */
export const WYDAN_W_TYLE_USTERKA = 3;

/** Co dziesiąta klatka ponad budżetem jest widoczna gołym okiem przy wadze. */
export const KLATEK_PONAD_BUDZET_UWAGA = 0.1;

/**
 * Od ilu milisekund opóźnienia odczytu wagi ekran mówi „uwaga".
 *
 * ŚWIADOMIE WYŻSZY NIŻ BUDŻET PANELU (`budzetFiltrowMs` = 250 ms
 * w `apps/fabrica-panel/lib/rdzen/opoznienie.dart`) i to nie jest rozjazd —
 * to są DWA RÓŻNE PYTANIA, tak samo jak `MILCZY_MIN` (60 min, ekran) i
 * `MILCZY_ALERT_H` (24 h, skrzynka). Budżet panelu pyta „czy TEN zestaw
 * filtrów wolno wpisać do modułu" i jest progiem projektowym. Ta stała pyta
 * „czy warto o tym stanowisku rozmawiać" i jest progiem WIDOCZNOŚCI: 400 ms
 * to tyle, po ile człowiek przy stole zaczyna czekać na liczbę, zamiast ją
 * czytać. Stanowisko między 250 a 400 ms ma filtry do poprawienia, ale nie
 * ma usterki — i lista, która by to pokazywała na czerwono, uczyłaby
 * przewijać czerwień.
 */
export const OPOZNIENIE_UWAGA_MS = 400;

/**
 * Od ilu milisekund to już nie jest „do poprawienia", tylko zepsute.
 *
 * Sekunda z okładem znaczy, że moduł albo chodzi w 10 SPS, albo ma okna
 * z górnej połowy zakresu — w obu przypadkach naważanie porcjami przestaje
 * być możliwe, bo człowiek dosypuje na ślepo przez ten cały czas.
 */
export const OPOZNIENIE_USTERKA_MS = 1000;

/**
 * Po ilu dniach pomiar opóźnienia przestaje coś znaczyć.
 *
 * Filtry zmienia kalibracja, a kalibracja bywa raz na kwartał — ale zmienia
 * je też człowiek programem producenta, przy module, bez śladu u nas. Pomiar
 * sprzed pół roku opisuje więc wagę, której mogło już nie być; po tym progu
 * mówimy „nie wiadomo", a nie „w porządku".
 */
export const OPOZNIENIE_WAZNE_DNI = 180;

/**
 * Od ilu procent baterii testera BLE ekran mówi „uwaga".
 *
 * DWADZIEŚCIA, bo tyle zostaje czasu na reakcję. CR2032 w HI9810382 starcza
 * na ~1000 h pracy, a przy włączonym Bluetoothie na ~500 — czyli ostatnie
 * 20 % to kilkadziesiąt godzin, mniej więcej tydzień zmian. Tyle trzeba, żeby
 * ktoś kupił baterię i ją wymienił, zamiast szukać jej w dniu, w którym
 * tester zgasł w środku pomiaru.
 */
export const BATERIA_UWAGA_PROC = 20;

/**
 * Od ilu procent to już usterka, a nie uwaga.
 *
 * DZIESIĘĆ. Poniżej tego progu tester gaśnie w trakcie zmiany, a pomiar
 * przerwany w połowie jest gorszy niż niezrobiony: kto go zaczął, ten myśli,
 * że go ma.
 */
export const BATERIA_USTERKA_PROC = 10;

/**
 * Po ilu dniach ostatni odczyt baterii przestaje cokolwiek znaczyć.
 *
 * SIEDEM — i to jest próg, który tu robi najwięcej roboty. Telemetria
 * powstaje WYŁĄCZNIE wtedy, gdy panel jest połączony z testerem: dziś po
 * odkrywce (czynność serwisowa, rzadka), po etapie G5 także po każdym
 * pomiarze. Odczyt sprzed dwóch tygodni opisuje więc baterię, której mogło
 * już nie być — albo taką, którą ktoś w międzyczasie wymienił.
 *
 * Po tym progu mówimy „nie wiadomo", a NIE „w porządku". To jest ta sama
 * decyzja, co przy `OPOZNIENIE_WAZNE_DNI`, i ten sam powód: liczba bez daty
 * ważności kłamie tym pewniej, im jest starsza.
 */
export const BATERIA_WAZNA_DNI = 7;

// ---------------------------------------------------------------------------

/**
 * KOLEJNOŚĆ MA ZNACZENIE — sortowanie liczy `indexOf` na tej liście.
 *
 * `usterka` — wiem, że jest źle;
 * `uwaga`   — jest podejrzanie, ale to może być normalny stan;
 * `niewiadomo` — stanowisko nie umie o tym powiedzieć (stara binarka);
 * `sprawne` — zmierzone i w porządku;
 * `nieczynne` — licencja bez sparowanego urządzenia. Nie jest zepsuta,
 *               jeszcze nie zaczęła istnieć.
 */
export const POWAGI = ['usterka', 'uwaga', 'niewiadomo', 'sprawne', 'nieczynne'] as const;
export type Powaga = (typeof POWAGI)[number];

/** Klucz usterki — stały, bo po nim filtruje ekran i po nim będzie alert (`#69`). */
export type KluczUsterki =
  | 'milczy'
  | 'symulator'
  | 'agent'
  | 'wersja'
  | 'wersja_agenta'
  | 'automat'
  | 'zegar'
  | 'wywrotki'
  | 'bledy'
  | 'kolejka'
  | 'klatki'
  | 'opoznienie'
  | 'bateria';

export interface UsterkaFloty {
  klucz: KluczUsterki;
  powaga: Extract<Powaga, 'usterka' | 'uwaga'>;
  /** Zdanie dla człowieka. Ma odpowiadać na „co jest zepsute", nie nazywać pola. */
  zdanie: string;
}

export interface StanowiskoDoOceny {
  id: string;
  nazwa: string | null;
  license_code: string;
  device_id: string | null;
  last_heartbeat_at: string | null;
  /** Nazwa organizacji — wypełniona tylko na ekranie platformy. */
  organizacja?: string | null;
  puls: PanelPulsFacts | null;
  /**
   * Wagi tego stanowiska z kartoteki przyrządów (`fabrica_instruments`,
   * kolumny opóźnienia z migracji `726`).
   *
   * NIE Z PULSU, i to jest decyzja, nie oszczędność. Opóźnienie mierzy się
   * ręcznie, raz na jakiś czas, a puls leci co pięć minut z każdego stołu —
   * wożenie w nim liczby, która nie zmienia się między pomiarami, dałoby
   * DRUGIE miejsce, w którym stoi ta sama prawda. Kartoteka przyrządu jest
   * pierwszym i jedynym; tu tylko ją czytamy.
   *
   * `undefined` znaczy „ten ekran nie pytał o przyrządy" i jest czym innym
   * niż `[]` („stanowisko nie ma wag w rejestrze") — ocena milczy w obu
   * przypadkach, ale z dwóch różnych powodów.
   */
  przyrzady?: PrzyrzadDoOceny[];
}

/**
 * Jedna waga z kartoteki, w zakresie potrzebnym do werdyktu o płynności.
 *
 * Wszystkie pola opóźnienia są opcjonalne, bo przyrząd, którego nikt nie
 * zmierzył, jest stanem normalnym — pomiar wymaga człowieka z odważnikiem.
 */
export interface PrzyrzadDoOceny {
  id: string;
  kod: string | null;
  /** Ostatnie ZMIERZONE opóźnienie odczytu w ms. */
  opoznienie_ms?: number | null;
  /** Kiedy je zmierzono (ISO). Bez tego liczba nie ma daty ważności. */
  opoznienie_zmierzono_o?: string | null;
}

export interface OcenaStanowiska {
  stanowisko: StanowiskoDoOceny;
  powaga: Powaga;
  usterki: UsterkaFloty[];
  /** Czego ta wersja panelu nie umie powiedzieć. NIE jest to samo co „dobrze". */
  niewiadome: string[];
  /** Sekundy od ostatniego pulsu; `null`, gdy stanowisko nie odezwało się nigdy. */
  ciszaS: number | null;
  /** Ile wydań w tyle względem najnowszego opublikowanego; `null` = nie wiadomo. */
  wTyle: number | null;
}

export interface OpcjeOceny {
  /** Chwila odniesienia w ms. Podawana z zewnątrz, żeby ocena była testowalna. */
  teraz: number;
  /**
   * Najwyższy `version_code` opublikowany dla `fabrica-panel` w rejestrze wydań.
   *
   * ŚWIADOMIE JEDEN NA CAŁĄ FLOTĘ, nie jeden na platformę. Jeżeli wydanie na
   * którąś platformę padło, stanowiska tej platformy zaczną tu wyglądać na
   * zapóźnione — i to jest DOKŁADNIE to, co ma być widać. Windows stał na
   * `1.0.6` przez sześć wydań właśnie dlatego, że nikt nie porównywał tego,
   * co stoi w hali, z tym, co miało wyjechać.
   */
  najnowszyKodWersji?: number | null;
}

function sekundOd(iso: string | null | undefined, teraz: number): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.round((teraz - t) / 1000));
}

function czasPoLudzku(sekundy: number): string {
  if (sekundy < 60) return `${sekundy} s`;
  if (sekundy < 3600) return `${Math.round(sekundy / 60)} min`;
  if (sekundy < 86_400) return `${Math.round(sekundy / 3600)} h`;
  return `${Math.round(sekundy / 86_400)} d`;
}

/**
 * Dlaczego panel nie ma tokenu agenta — słownik `ZrodloTokenu`
 * z `apps/fabrica-panel/lib/dane/magazyn_tokena_agenta.dart`.
 *
 * Cztery z tych stanów do `1.0.61` nazywały się identycznie (`brak`) i każdy
 * ma INNĄ naprawę; przepisanie ich tu na zdania jest całym sensem `S129`.
 */
const POWOD_BRAKU_TOKENA: Record<string, string> = {
  brakPliku: 'agent nie wstał albo go nie ma',
  brakPrawa: 'plik tokena jest, panel nie ma prawa go przeczytać',
  bladOdczytu: 'system zgłosił błąd odczytu tokena',
  brakSciezki: 'ta platforma nie trzyma agenta lokalnie',
  brak: 'bez wyjaśnienia',
};

/**
 * Ocena JEDNEGO stanowiska. Czysta — bez `Date.now()`, bez sieci, bez Reacta.
 */
export function ocenStanowisko(stanowisko: StanowiskoDoOceny, opcje: OpcjeOceny): OcenaStanowiska {
  const puls = stanowisko.puls;
  const ciszaS = sekundOd(stanowisko.last_heartbeat_at, opcje.teraz);
  const usterki: UsterkaFloty[] = [];
  const niewiadome: string[] = [];

  // Licencja bez sparowanego urządzenia nie jest stanowiskiem — jest kartką
  // z kodem. Ocenianie jej dokładałoby do listy alarmów rzeczy, których nikt
  // nie zamierzał uruchomić.
  if (!stanowisko.device_id) {
    return {
      stanowisko,
      powaga: 'nieczynne',
      usterki: [],
      niewiadome: [],
      ciszaS,
      wTyle: null,
    };
  }

  // --- CISZA ---------------------------------------------------------------
  if (ciszaS === null) {
    usterki.push({
      klucz: 'milczy',
      powaga: 'usterka',
      zdanie: 'sparowane, ale nie odezwało się ani razu',
    });
  } else if (ciszaS >= MILCZY_MIN * 60) {
    usterki.push({
      klucz: 'milczy',
      powaga: 'usterka',
      zdanie: `milczy od ${czasPoLudzku(ciszaS)}`,
    });
  } else if (ciszaS >= CISZA_MIN * 60) {
    usterki.push({
      klucz: 'milczy',
      powaga: 'uwaga',
      zdanie: `cisza od ${czasPoLudzku(ciszaS)}`,
    });
  }

  // --- WAGA ----------------------------------------------------------------
  // Ta jedna usterka jest powodem, dla którego cały ekran powstał.
  const zrodloMasy = puls?.waga?.zrodlo;
  if (zrodloMasy === undefined) {
    niewiadome.push('źródło masy');
  } else if (zrodloMasy === 'symulator') {
    const powod = POWOD_BRAKU_TOKENA[puls?.waga?.token_agenta ?? ''];
    usterki.push({
      klucz: 'symulator',
      powaga: 'usterka',
      zdanie: powod ? `waży na symulatorze — ${powod}` : 'waży na symulatorze',
    });
  }

  // --- ROZMOWA Z AGENTEM ---------------------------------------------------
  // Osobno od `zrodlo`, bo to inne pytanie: tam „mam token", tu „doszło do
  // rozmowy". Stanowisko z tokenem i bez rozmowy waży na symulatorze, choć
  // `zrodlo` mówi `agent` — patrz komentarz przy `PanelPulsFacts.waga`.
  const polaczenie = puls?.waga?.polaczenie;
  if (polaczenie === undefined) {
    // Tylko wtedy, gdy panel w ogóle deklaruje agenta — na stanowisku
    // z symulatorem brak tej gałęzi nie jest niewiadomą, tylko nadmiarem.
    if (zrodloMasy === 'agent') niewiadome.push('połączenie z agentem');
  } else if (polaczenie === 'odmowaTokenu') {
    usterki.push({
      klucz: 'agent',
      powaga: 'usterka',
      zdanie: 'agent odrzucił token panelu — oba końce mają różne sekrety',
    });
  } else if (polaczenie === 'brakNasluchu') {
    usterki.push({
      klucz: 'agent',
      powaga: 'usterka',
      zdanie: 'agent nie nasłuchuje — usługa nie wstała albo padła',
    });
  } else if (polaczenie === 'blad') {
    usterki.push({
      klucz: 'agent',
      powaga: 'usterka',
      zdanie: 'połączenie z agentem zerwane z nieznanego powodu',
    });
  }
  // `nieProbowano` NIE jest usterką: nikt jeszcze nie zajrzał na wagę, więc
  // panel nie miał powodu się łączyć. Zamiana tego w alarm zapalałaby całą
  // halę o 05:00, zanim ktokolwiek weźmie pierwsze zadanie.

  // --- WERSJA --------------------------------------------------------------
  let wTyle: number | null = null;
  const kod = puls?.kod_wersji;
  const najnowszy = opcje.najnowszyKodWersji;
  if (kod === undefined) {
    niewiadome.push('wersja');
  } else if (najnowszy != null && najnowszy > kod) {
    wTyle = najnowszy - kod;
    usterki.push({
      klucz: 'wersja',
      powaga: wTyle >= WYDAN_W_TYLE_USTERKA ? 'usterka' : 'uwaga',
      zdanie:
        wTyle === 1
          ? `jedno wydanie w tyle (${puls?.wersja ?? kod})`
          : `${wTyle} wydania w tyle (${puls?.wersja ?? kod})`,
    });
  } else if (najnowszy != null) {
    wTyle = 0;
  }

  // --- AGENT W INNEJ WERSJI NIŻ PANEL --------------------------------------
  // Oba jadą jednym instalatorem pod jednym numerem, więc dwie różne liczby
  // znaczą jedno: agent nie wstał z nową paczką. Na Pi do 1.0.82 działo się to
  // po KAŻDYM OTA (`systemctl enable --now` nie restartuje działającej usługi)
  // i nie było tego widać znikąd, bo agent meldował stały literał `0.1.0`.
  //
  // Pytamy TYLKO przy stojącej rozmowie: bez niej numer i tak nie przyjdzie,
  // a rozmowa, której nie ma, ma własną usterkę wyżej. `uwaga`, nie `usterka`
  // — stan mija sam przy najbliższym restarcie maszyny, a od 1.0.83 nie
  // powstaje wcale; mail o czymś, co mija, uczy kasowania maili.
  const wersjaAgenta = puls?.waga?.wersja_agenta;
  if (polaczenie === 'polaczony') {
    if (wersjaAgenta === undefined) {
      niewiadome.push('wersja agenta');
    } else if (puls?.wersja !== undefined && wersjaAgenta !== puls.wersja) {
      usterki.push({
        klucz: 'wersja_agenta',
        powaga: 'uwaga',
        zdanie: `agent wagi w wersji ${wersjaAgenta}, panel ${puls.wersja} — agent nie wstał po aktualizacji`,
      });
    }
  }

  // --- AUTOMAT AKTUALIZACJI ------------------------------------------------
  // Czy wydania mają czym wejść. Do 1.0.82 stanowisko z martwym automatem
  // wyglądało z serwera identycznie jak stanowisko, na którym nikt nie
  // włączył aktualizacji — a to dwie różne wizyty serwisanta: pierwsza pyta,
  // co zabiło zadanie, druga zakłada je od nowa jednym kliknięciem UAC.
  //
  // `martwy` jest usterką i jedzie mailem: zadanie BYŁO i przestało się
  // budzić, samo nie wróci. `brak` jest uwagą: na świeżo postawionym Windowsie
  // tętno pojawia się dopiero po pierwszym obudzeniu zadania (do dwóch minut
  // po instalacji), więc pierwszy puls po instalacji mówi `brak` zgodnie
  // z prawdą i przez chwilę — mail o tym uczyłby kasowania maili.
  const automat = puls?.aktualizacja?.automat;
  if (automat === undefined) {
    niewiadome.push('automat aktualizacji');
  } else if (automat === 'martwy') {
    const tetno = puls?.aktualizacja?.tetno_s;
    usterki.push({
      klucz: 'automat',
      powaga: 'usterka',
      zdanie:
        tetno != null
          ? `automat aktualizacji przestał się budzić (ostatnie tętno ${czasPoLudzku(tetno)} temu) — wydania nie wejdą same`
          : 'automat aktualizacji przestał się budzić — wydania nie wejdą same',
    });
  } else if (automat === 'brak') {
    usterki.push({
      klucz: 'automat',
      powaga: 'uwaga',
      zdanie: 'stanowisko nie ma automatu aktualizacji — wydania nie wejdą same',
    });
  }

  // --- ZEGAR ---------------------------------------------------------------
  const rozjazd = puls?.zegar?.rozjazd_s;
  if (rozjazd === undefined) {
    niewiadome.push('zegar');
  } else if (Math.abs(rozjazd) >= ROZJAZD_PROG_S) {
    usterki.push({
      klucz: 'zegar',
      powaga: 'usterka',
      zdanie: `zegar rozjechany o ${czasPoLudzku(Math.abs(Math.round(rozjazd)))}`,
    });
  }

  // --- WYWROTKI I BŁĘDY ----------------------------------------------------
  // Gałąź `bledy` istnieje od 1.0.67. Jej BRAK to „nie umiem odpowiedzieć",
  // a nie „zero błędów" — patrz nagłówek pliku.
  if (puls?.bledy === undefined) {
    niewiadome.push('błędy');
  } else {
    const wywrotek = puls.bledy.wywrotek_startu;
    if (wywrotek === undefined) {
      niewiadome.push('wywrotki startu');
    } else if (wywrotek > 0) {
      usterki.push({
        klucz: 'wywrotki',
        powaga: 'usterka',
        zdanie:
          wywrotek === 1
            ? 'ostatni start zakończył się wywrotką'
            : `${wywrotek} wywrotek pod rząd przy starcie`,
      });
    }

    const w24h = puls.bledy.w_24h;
    if (w24h === undefined) {
      niewiadome.push('błędy w dobie');
    } else if (w24h > 0) {
      usterki.push({
        klucz: 'bledy',
        powaga: w24h >= BLEDOW_24H_USTERKA ? 'usterka' : 'uwaga',
        zdanie: `${w24h} ${w24h === 1 ? 'błąd' : 'błędów'} w ostatniej dobie`,
      });
    }
  }

  // --- SKRZYNKA NADAWCZA ---------------------------------------------------
  const lacznosc = puls?.lacznosc;
  if (lacznosc?.kolejka_urwana) {
    usterki.push({
      klucz: 'kolejka',
      powaga: 'usterka',
      zdanie: 'skrzynka nadawcza się urwała — zapisy przepadają',
    });
  } else if ((lacznosc?.w_kolejce ?? 0) >= W_KOLEJCE_UWAGA) {
    usterki.push({
      klucz: 'kolejka',
      powaga: 'uwaga',
      zdanie: `${lacznosc?.w_kolejce} zapisów czeka w kolejce`,
    });
  }

  // --- PŁYNNOŚĆ ------------------------------------------------------------
  const klatki = puls?.klatki;
  if (klatki?.zmierzonych && klatki.ponad_budzet !== undefined) {
    const udzial = klatki.ponad_budzet / klatki.zmierzonych;
    if (udzial >= KLATEK_PONAD_BUDZET_UWAGA) {
      usterki.push({
        klucz: 'klatki',
        powaga: 'uwaga',
        zdanie: `${Math.round(udzial * 100)}% klatek ponad budżetem`,
      });
    }
  }

  // --- OPÓŹNIENIE ODCZYTU WAGI (migracja `726`) ----------------------------
  //
  // TYLKO EKRAN, NIGDY SKRZYNKA. Alert mailowy ma sens wtedy, gdy trzeba po
  // kogoś iść — a po opóźnienie nikt nie idzie: ono nie psuje się samo, nie
  // mija samo i naprawia je człowiek, który i tak stanie przy tym stole
  // z odważnikiem. Mail o nim byłby nauką kasowania bez czytania.
  //
  // Nie ma tu `?? 0` i nie będzie: przyrząd niezmierzony to `niewiadomo`,
  // a nie `sprawne`. Zieleń tam, gdzie nie ma wiedzy, jest dokładnie tą
  // usterką, którą `token_agenta` kosztowało pół dnia zgadywania.
  if (stanowisko.przyrzady !== undefined) {
    for (const p of stanowisko.przyrzady) {
      const ms = p.opoznienie_ms;
      const zmierzono = p.opoznienie_zmierzono_o;
      const nazwa = p.kod ?? p.id;
      if (ms === undefined || ms === null || !zmierzono) {
        niewiadome.push(`opóźnienie odczytu (${nazwa})`);
        continue;
      }
      const wiekDni = (opcje.teraz - Date.parse(zmierzono)) / 86_400_000;
      if (!Number.isFinite(wiekDni) || wiekDni > OPOZNIENIE_WAZNE_DNI) {
        niewiadome.push(`opóźnienie odczytu (${nazwa}) — pomiar przeterminowany`);
        continue;
      }
      if (ms >= OPOZNIENIE_UWAGA_MS) {
        usterki.push({
          klucz: 'opoznienie',
          powaga: ms >= OPOZNIENIE_USTERKA_MS ? 'usterka' : 'uwaga',
          zdanie: `${nazwa}: odczyt spóźnia się o ${Math.round(ms)} ms`,
        });
      }
    }
  }

  // BATERIA TESTERA BLE — czytana z ostatniej telemetrii, nie odpytywana.
  //
  // Gałąź OPCJONALNA po stronie serwera i to jest reguła, nie wygoda: panele
  // jadą OTA i przez kilka dni po wydaniu część stanowisk przysyła puls bez
  // niej. Brak gałęzi znaczy „ta wersja nie umie odpowiedzieć", a nie
  // „bateria zero" — `?? 0` pokazałby tu alarm na każdym stanowisku ze
  // starszym panelem.
  const bat = puls?.ble?.bateria;
  if (bat && typeof bat.proc === 'number') {
    const nazwa = bat.adres ?? 'tester BLE';
    const wiekDni = typeof bat.o === 'number' ? (opcje.teraz - bat.o) / 86_400_000 : Number.NaN;
    if (!Number.isFinite(wiekDni) || wiekDni > BATERIA_WAZNA_DNI) {
      // Odczyt bez daty albo przeterminowany to „nie wiadomo", NIE
      // „w porządku". Bateria zmierzona dwa tygodnie temu opisuje stan,
      // którego mogło już nie być — albo taki, który ktoś w międzyczasie
      // naprawił, wymieniając ogniwo.
      niewiadome.push(`bateria testera (${nazwa}) — odczyt przeterminowany`);
    } else if (bat.proc <= BATERIA_UWAGA_PROC) {
      // `<=`, a nie `<`: dokładnie 20 % jest już stanem, o którym warto
      // powiedzieć. I porównanie na LICZBIE, nie na prawdziwości — `if (proc)`
      // odsiałoby zero, czyli stan najgroźniejszy ze wszystkich.
      usterki.push({
        klucz: 'bateria',
        powaga: bat.proc <= BATERIA_USTERKA_PROC ? 'usterka' : 'uwaga',
        zdanie: `${nazwa}: bateria testera ${bat.proc}%`,
      });
    }
  }

  const powaga: Powaga = usterki.some((u) => u.powaga === 'usterka')
    ? 'usterka'
    : usterki.length > 0
      ? 'uwaga'
      : niewiadome.length > 0
        ? 'niewiadomo'
        : 'sprawne';

  return { stanowisko, powaga, usterki, niewiadome, ciszaS, wTyle };
}

/**
 * Kolejność floty: najpierw to, czym trzeba się zająć.
 *
 * NIE ALFABETYCZNIE i nie po dacie parowania. Lista posortowana nazwą wymaga
 * przeczytania wszystkich wierszy, żeby znaleźć ten jeden zepsuty — a wtedy
 * przy dwudziestu czterech stanowiskach nikt jej nie czyta i wracamy do
 * tygodnia ważenia na symulatorze.
 *
 * Rozstrzygnięcia po kolei: powaga → liczba usterek → długość ciszy → nazwa.
 * Ostatni człon jest po to, żeby kolejność była STABILNA: lista, która skacze
 * przy każdym odświeżeniu, jest listą, w którą trudno kliknąć.
 */
export function posortujFlote(oceny: OcenaStanowiska[]): OcenaStanowiska[] {
  return [...oceny].sort((a, b) => {
    const pa = POWAGI.indexOf(a.powaga);
    const pb = POWAGI.indexOf(b.powaga);
    if (pa !== pb) return pa - pb;
    if (a.usterki.length !== b.usterki.length) return b.usterki.length - a.usterki.length;
    const ca = a.ciszaS ?? Number.MAX_SAFE_INTEGER;
    const cb = b.ciszaS ?? Number.MAX_SAFE_INTEGER;
    if (ca !== cb) return cb - ca;
    const na = a.stanowisko.nazwa ?? a.stanowisko.license_code;
    const nb = b.stanowisko.nazwa ?? b.stanowisko.license_code;
    return na.localeCompare(nb, 'pl');
  });
}

export type PodsumowanieFloty = Record<Powaga, number> & { usterek: number };

/** Liczniki nad tabelą — pięć kubełków powagi plus łączna liczba usterek. */
export function podsumujFlote(oceny: OcenaStanowiska[]): PodsumowanieFloty {
  const wynik = {
    usterka: 0,
    uwaga: 0,
    niewiadomo: 0,
    sprawne: 0,
    nieczynne: 0,
    usterek: 0,
  } as PodsumowanieFloty;
  for (const o of oceny) {
    wynik[o.powaga] += 1;
    wynik.usterek += o.usterki.length;
  }
  return wynik;
}

/**
 * Najwyższy `version_code` z odpowiedzi rejestru wydań.
 *
 * `get_app_version` na brak wydania zwraca `{update_available:false}` — obiekt
 * PRAWDZIWY i pusty, więc odsiew musi iść po obecności `version_code`, a nie
 * po prawdziwości obiektu. Zwraca `null`, gdy nie opublikowano NICZEGO:
 * porównywanie z zerem kazałoby całej hali wyglądać na aktualną.
 */
export function najnowszyKodWersji(
  wydania: Record<string, { version_code?: number } | undefined>,
  klucze: string[]
): number | null {
  let max: number | null = null;
  for (const klucz of klucze) {
    const kod = wydania[klucz]?.version_code;
    if (typeof kod === 'number' && (max === null || kod > max)) max = kod;
  }
  return max;
}

/**
 * Werdykt o BLE stanowiska — dla EKRANU SERWERA, nie dla człowieka przy stole.
 *
 * DWA PYTANIA, NIE JEDNO. Panel odpowiada przy stole na „co mam teraz zrobić"
 * (`zdanieOdmowyBle` w `rdzen/znalezione_ble.dart`) i mówi całym zdaniem,
 * łącznie z komendą do wpisania. Ten opis odpowiada na „czy to stanowisko
 * nadaje się do przyrządów Bluetooth" i mieści się w komórce tabeli floty.
 * Reguła „jeden werdykt jeden plik" zakazuje dwóch odpowiedzi na TO SAMO
 * pytanie — tak samo jak przy ciszy, gdzie ekran ma próg 60 min, a skrzynka
 * 24 h, i to jest poprawne.
 *
 * TRZY POZIOMY „NIE WIEM" (patrz `PanelPulsFacts.ble`) mają WŁASNE odpowiedzi
 * i żadna z nich nie jest zerem: `?? 'brak'` pokazałby zmierzony werdykt tam,
 * gdzie nikt nie pytał.
 */
export function opisBle(ble: PanelPulsFacts['ble']): {
  wartosc: string;
  ton: 'zwykly' | 'uwaga';
  pod?: string;
} {
  if (!ble) return { wartosc: 'nie podaje', ton: 'zwykly', pod: 'panel starszy niż etap BLE' };
  if (ble.agent_oglasza !== true) {
    return { wartosc: 'nie podaje', ton: 'zwykly', pod: 'agent nie stoi albo jest starszy' };
  }
  if ((ble.zdolnosci ?? []).includes('ble')) {
    return { wartosc: 'gotowe', ton: 'zwykly' };
  }
  // Od tej linii wiemy, że agent OGŁOSIŁ brak zdolności. Cztery przyczyny —
  // i dopiero one decydują, czy ktoś ma jechać na stanowisko z donglem.
  if (ble.stos === 'brak') {
    // POWÓD WYPIERA OGÓLNIK, GDY JEST — i tylko wtedy.
    //
    // „Usterka wydania” mówi, że nie warto jechać z donglem; nie mówi, od czego
    // zacząć naprawę. Rozpoznanie usterki z 21.09.2026 wymagało ściągnięcia
    // paczki z produkcji i rozpakowania jej ręcznie, bo zdanie agenta leżało
    // wyłącznie na `stderr` stanowiska. Teraz jedzie pulsem — ale pole jest
    // opcjonalne, więc ogólnik zostaje dla stanowisk sprzed 1.0.88.
    const powod = typeof ble.powod === 'string' ? ble.powod.trim() : '';
    return {
      wartosc: 'brak stosu',
      ton: 'uwaga',
      pod: powod === '' ? 'usterka wydania — dongiel nie pomoże' : powod,
    };
  }
  if (ble.radio === 'brak')
    return { wartosc: 'brak adaptera', ton: 'uwaga', pod: 'potrzebny dongiel' };
  if (ble.radio === 'wylaczone') {
    return { wartosc: 'radio wyłączone', ton: 'uwaga', pod: 'rfkill unblock bluetooth' };
  }
  // `nieznane` NIE jest usterką: stos jeszcze nie odpowiedział i sam się
  // odezwie. Ton `uwaga` wysyłałby serwisanta do stanowiska, które naprawia
  // się samo w ciągu sekund.
  if (ble.radio === 'nieznane') {
    return { wartosc: 'radio wstaje', ton: 'zwykly', pod: 'stos jeszcze nie odpowiedział' };
  }
  return { wartosc: 'bez radia', ton: 'uwaga', pod: 'agent starszy niż diagnostyka' };
}
